import React, { useState, useEffect } from 'react';
import { api } from '../api';

/* ─── Styles Minimalistes SaaS ────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,700&family=Karla:wght@400;600;700&display=swap');

  :root {
    --bg: #080604;
    --card: #111009;
    --border: #2C2518;
    --copper: #C97C2A;
    --amber: #E8A03A;
    --cream: #F2E6D0;
    --muted: #7A6E5E;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .ako-root {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg);
    font-family: 'Karla', sans-serif;
    color: var(--cream);
    padding: 1.5rem;
    position: relative;
  }

  .ako-root::before {
    content: '';
    position: fixed; inset: 0;
    opacity: 0.03; pointer-events: none;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    background-size: 180px;
  }

  .ako-login-card {
    width: 100%;
    max-width: 420px;
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 20px;
    padding: 2.5rem;
    box-shadow: 0 20px 50px rgba(0,0,0,0.5);
    z-index: 1;
    animation: fadeIn 0.6s ease-out;
  }

  @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

  .ako-brand {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 2.5rem;
  }
  .ako-logo-mark {
    width: 48px; height: 48px;
    background: linear-gradient(135deg, var(--copper), var(--amber));
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Fraunces', serif;
    font-weight: 700; font-size: 1.5rem; color: #000;
  }
  .ako-brand-name { font-family: 'Fraunces', serif; font-size: 1.4rem; font-weight: 600; color: var(--cream); }

  .ako-tabs {
    display: flex;
    background: rgba(255,255,255,0.03);
    padding: 0.3rem;
    border-radius: 10px;
    margin-bottom: 2rem;
  }
  .ako-tab {
    flex: 1;
    padding: 0.6rem;
    border: none;
    background: none;
    color: var(--muted);
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.2s;
  }
  .ako-tab.active { background: var(--border); color: var(--cream); }

  .ako-field { margin-bottom: 1.25rem; }
  .ako-label { display: block; font-size: 0.75rem; font-weight: 700; color: var(--muted); text-transform: uppercase; margin-bottom: 0.5rem; }
  .ako-input, .ako-select {
    width: 100%;
    background: rgba(0,0,0,0.2);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 0.9rem 1rem;
    color: var(--cream);
    font-size: 1rem;
    outline: none;
    transition: border-color 0.2s;
  }
  .ako-input:focus, .ako-select:focus { border-color: var(--copper); }

  .ako-btn {
    width: 100%;
    padding: 1rem;
    border-radius: 10px;
    border: none;
    background: linear-gradient(135deg, var(--copper), var(--amber));
    color: #000;
    font-weight: 700;
    font-size: 0.95rem;
    cursor: pointer;
    margin-top: 1rem;
    transition: opacity 0.2s;
  }
  .ako-btn:hover { opacity: 0.9; }

  .ako-forgot { text-align: center; margin-top: 1.5rem; }
  .ako-forgot button { background: none; border: none; color: var(--muted); font-size: 0.85rem; cursor: pointer; }
  
  .ako-error { background: rgba(255,0,0,0.1); color: #ff8080; padding: 0.8rem; border-radius: 8px; font-size: 0.85rem; margin-bottom: 1rem; text-align: center; }
  .ako-success { background: rgba(0,255,0,0.1); color: #80ff80; padding: 0.8rem; border-radius: 8px; font-size: 0.85rem; margin-bottom: 1rem; text-align: center; }
`;

const Login = ({ onLogin, onRegister }) => {
  const [mode, setMode] = useState(() => {
    // Support ?signup=1 : ouvre directement le formulaire d'inscription (CTA landing)
    try {
      if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('signup') === '1') {
        return 'signup';
      }
    } catch (e) {}
    return 'login';
  }); // 'login', 'signup', 'forgot'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [profession, setProfession] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const tag = document.createElement('style');
    tag.textContent = STYLES;
    document.head.appendChild(tag);
    return () => tag.remove();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      if (mode === 'forgot') {
        await apiFetch('/auth/forgot-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        setSuccess('Lien envoyé ! Vérifiez vos emails.');
      } else if (mode === 'login') {
        const result = await onLogin(email, password);
        if (!result.success) setError(result.error || 'Identifiants incorrects');
      } else {
        const result = await onRegister(name, email, password, '', profession, company);
        if (!result.success) setError(result.error || "Erreur lors de l'inscription");
      }
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const apiFetch = async (url, options) => {
    const API_BASE = import.meta.env.VITE_API_URL || '';
    const res = await fetch(API_BASE + url, options);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erreur');
    return data;
  };

  return (
    <div className="ako-root">
      <div className="ako-login-card">
        <div className="ako-brand">
          <div className="ako-logo-mark">A</div>
          <span className="ako-brand-name">AKO Digital</span>
        </div>

        {mode !== 'forgot' && (
          <div className="ako-tabs">
            <button className={`ako-tab ${mode === 'login' ? 'active' : ''}`} onClick={() => setMode('login')}>Connexion</button>
            <button className={`ako-tab ${mode === 'signup' ? 'active' : ''}`} onClick={() => setMode('signup')}>Inscription</button>
          </div>
        )}

        {error && <div className="ako-error">{error}</div>}
        {success && <div className="ako-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          {mode === 'signup' && (
            <>
              <div className="ako-field">
                <label className="ako-label">Nom complet</label>
                <input className="ako-input" value={name} onChange={e => setName(e.target.value)} placeholder="Prénom Nom" required />
              </div>
              <div className="ako-field">
                <label className="ako-label">Entreprise</label>
                <input className="ako-input" value={company} onChange={e => setCompany(e.target.value)} placeholder="Nom de votre société" required />
              </div>
              <div className="ako-field">
                <label className="ako-label">Métier</label>
                <select className="ako-select" value={profession} onChange={e => setProfession(e.target.value)} required>
                  <option value="">Sélectionnez votre métier</option>
                  <option value="Électricien">Électricien</option>
                  <option value="Plombier">Plombier</option>
                  <option value="Menuisier">Menuisier</option>
                  <option value="Maçon">Maçon</option>
                  <option value="Peintre">Peintre</option>
                  <option value="Autre">Autre / BTP</option>
                </select>
              </div>
            </>
          )}

          <div className="ako-field">
            <label className="ako-label">Email</label>
            <input className="ako-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.fr" required />
          </div>

          {mode !== 'forgot' && (
            <div className="ako-field">
              <label className="ako-label">Mot de passe</label>
              <input className="ako-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
          )}

          <button type="submit" className="ako-btn" disabled={loading}>
            {loading ? 'Patientez...' : mode === 'login' ? 'Se connecter' : mode === 'signup' ? 'Créer mon compte' : 'Récupérer mon accès'}
          </button>
        </form>

        <div className="ako-forgot">
          {mode === 'login' ? (
            <button onClick={() => setMode('forgot')}>Mot de passe oublié ?</button>
          ) : (
            <button onClick={() => setMode('login')}>← Retour à la connexion</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
